import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toPaise } from '@vns/core';
import { supabase } from './supabase';
import { useSession } from './session';
import type { NewSupplier, NewSubcontractor, NewMaterial, BankFields } from './directory';
import {
  assertDeletable, guardSupplier, guardSubcontractor, guardMaterial, guardBankAccount,
} from './delete-guards';

/**
 * Editing and removing directory records.
 *
 * Deletion is always a soft delete — the row keeps its history and gains a
 * deleted_at stamp, and every list filters those out. Before removing anything,
 * each mutation counts what refers to it and refuses with a sentence explaining
 * the consequence. A supplier with purchases against them cannot simply vanish:
 * the material and finance reports would silently change.
 */

const NOW = () => new Date().toISOString();

function bankPayload(v: BankFields) {
  return {
    bank_name: v.bank_name?.trim() || null,
    bank_holder: v.bank_holder?.trim() || null,
    bank_account_no: v.bank_account_no?.trim() || null,
    bank_ifsc: v.bank_ifsc?.trim().toUpperCase() || null,
    bank_branch: v.bank_branch?.trim() || null,
  };
}

const check = (error: { message: string } | null) => {
  if (error) throw new Error(error.message);
};

/* -------------------------------- supplier -------------------------------- */

export function useUpdateSupplier() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async ({ id, ...v }: NewSupplier & { id: string }) => {
      if (!actor) throw new Error('Not signed in');

      check((await supabase.from('suppliers').update({
        name: v.name.trim(),
        contact_person: v.contact_person?.trim() || null,
        phone: v.phone.trim(),
        email: v.email?.trim() || null,
        address: v.address?.trim() || null,
        city: v.city?.trim() || null,
        gst_number: v.gst_number?.trim().toUpperCase() || null,
        remarks: v.remarks?.trim() || null,
        ...bankPayload(v),
      }).eq('id', id)).error);

      // Replace the whole link set rather than diffing it — simpler, and the
      // result is the same whichever boxes changed.
      check((await supabase.from('supplier_material_types').delete().eq('supplier_id', id)).error);
      const catIds = v.materialCategoryIds ?? [];
      if (catIds.length) {
        check((await supabase.from('supplier_material_types').insert(
          catIds.map((material_category_id) => ({
            supplier_id: id, material_category_id, company_id: actor.companyId,
          })),
        )).error);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

export function useDeleteSupplier() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      // The same guard the dialog ran, re-checked at write time in case
      // something was added in between.
      await assertDeletable(guardSupplier(id));
      check((await supabase.from('suppliers').update({ deleted_at: NOW() }).eq('id', id)).error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['suppliers'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

/* ----------------------------- sub-contractor ----------------------------- */

export function useUpdateSubcontractor() {
  const qc = useQueryClient();
  const actor = useSession((s) => s.actor);

  return useMutation({
    mutationFn: async ({ id, ...v }: NewSubcontractor & { id: string }) => {
      if (!actor) throw new Error('Not signed in');

      check((await supabase.from('subcontractors').update({
        name: v.name.trim(),
        phone: v.phone.trim(),
        email: v.email?.trim() || null,
        address: v.address?.trim() || null,
        city: v.city?.trim() || null,
        gst_number: v.gst_number?.trim().toUpperCase() || null,
        remarks: v.remarks?.trim() || null,
        ...bankPayload(v),
      }).eq('id', id)).error);

      check((await supabase.from('subcontractor_work_types').delete().eq('subcontractor_id', id)).error);
      if (v.workTypeIds.length) {
        check((await supabase.from('subcontractor_work_types').insert(
          v.workTypeIds.map((work_type_id) => ({
            subcontractor_id: id, work_type_id, company_id: actor.companyId,
          })),
        )).error);
      }
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

export function useDeleteSubcontractor() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await assertDeletable(guardSubcontractor(id));
      check((await supabase.from('subcontractors').update({ deleted_at: NOW() }).eq('id', id)).error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['subcontractors'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

/* -------------------------------- material -------------------------------- */

export function useUpdateMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: NewMaterial & { id: string }) => {
      check((await supabase.from('materials').update({
        name: v.name.trim(),
        brand: v.brand?.trim() || null,
        // category_id is deliberately absent, not null: omitting it leaves any
        // existing value alone. Sending null would wipe it on every save.
        unit_id: v.unit_id || null,
        rate_paise: v.rateRupees.trim() ? toPaise(v.rateRupees) : 0,
        supplier_id: v.supplier_id || null,
        opening_qty: v.opening_qty?.trim() ? Number(v.opening_qty) : 0,
      }).eq('id', id)).error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

export function useDeleteMaterial() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await assertDeletable(guardMaterial(id));
      check((await supabase.from('materials').update({ deleted_at: NOW() }).eq('id', id)).error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['materials'] });
      void qc.invalidateQueries({ queryKey: ['material'] });
      void qc.invalidateQueries({ queryKey: ['material-full-report'] });
      void qc.invalidateQueries({ queryKey: ['material-report-picker'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
      void qc.invalidateQueries({ queryKey: ['project-stock-map'] });
      void qc.invalidateQueries({ queryKey: ['project'] });
      void qc.invalidateQueries({ queryKey: ['report'] });
      void qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
}

/* ------------------------------ bank account ------------------------------ */

export function useUpdateBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({ id, ...v }: { id: string; bank_name: string; account_holder: string; branch?: string; account_no?: string; ifsc?: string; is_cash: boolean }) => {
      check((await supabase.from('bank_accounts').update({
        bank_name: v.bank_name.trim(),
        account_holder: v.account_holder.trim(),
        branch: v.branch?.trim() || null,
        account_no: v.account_no?.trim() || null,
        ifsc: v.ifsc?.trim().toUpperCase() || null,
        is_cash: v.is_cash,
      }).eq('id', id)).error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['bank-accounts'] });
      void qc.invalidateQueries({ queryKey: ['lookups'] });
    },
  });
}

export function useDeleteBankAccount() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (id: string) => {
      await assertDeletable(guardBankAccount(id));
      check((await supabase.from('bank_accounts').update({ deleted_at: NOW() }).eq('id', id)).error);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['bank-accounts'] }),
  });
}
